import React, { useState, useEffect, useRef } from "react";
import cn from "classnames";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import { format } from "date-fns";

import { ReactComponent as DefaultCalendarIcon } from "../../../media/icons/calendarNew.svg";
import { getSafeErrorText } from "../../../helpers/safeRender";
import { useListingEditReadOnly } from "../../../contexts/listingEditContext";
import styles from "./calendarWithInputMask.module.css";

const CalendarWithInputMask = ({
  placeholder,
  error,
  isInvalid,
  value,
  onChange,
  handleSubmit,
  handleChangeInput,
  mask = "99/99/9999",
  isYear,
  classNameError,
  minDate,
  defaultMaxDate,
  /** When false, a full typed complete date is accepted even if after maxDate; calendar picker still respects maxDate. */
  enforceMaxDateOnTypedComplete = true,
  isHideFeatureDate,
  inputClassName,
  icon: Icon = DefaultCalendarIcon,
  /** MDY_SLASH = MM/DD/YYYY; DMY_SLASH = DD/MM/YYYY; DMY_DOT = DD.MM.YYYY */
  dateFormat = "MDY_SLASH",
  openDirection = "down",
  /** Id of the element whose text names the date input; omitted by callers that name it another way. */
  ariaLabelledBy,
  /** Name for a date input no visible element names; ariaLabelledBy wins when both are passed. */
  ariaLabel,
  /** `id` and `name` of the date input, set only when passed. */
  id,
  name,
  /** Id for the error text. Only a caller that passes it gets the invalid field's aria-invalid and aria-describedby. */
  errorId
}) => {
  const readOnly = useListingEditReadOnly();
  const [selectedDate, setSelectedDate] = useState(null);
  const [isCalendarVisible, setIsCalendarVisible] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const calendarRef = useRef();
  const isFutureDate = date => date > new Date();

  const isDmyOrder = dateFormat === "DMY_DOT" || dateFormat === "DMY_SLASH";
  const separator = dateFormat === "DMY_DOT" ? "." : "/";

  const SUBMIT_DELAY_MS = 100;

  const scheduleSubmitIfPresent = () => {
    if (!handleSubmit) {
      return;
    }
    window.setTimeout(() => {
      handleSubmit();
    }, SUBMIT_DELAY_MS);
  };

  const handleDateChange = date => {
    setSelectedDate(date);
    setInputValue(formatDate(date));
    setIsCalendarVisible(false);
    onChange(formatDate(date));
    scheduleSubmitIfPresent();
  };

  const formatDate = date => {
    if (!date) return "";

    if (mask.length === 4) {
      return date.length === 4 ? date : new Date(String(date)).getFullYear();
    }

    if (typeof date === "string") {
      const parsed = parseDateFromInput(date);
      if (!parsed) return "";
      // eslint-disable-next-line no-param-reassign
      date = parsed;
    }

    const day = date
      .getDate()
      .toString()
      .padStart(2, "0");
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const year = new Date(date).getFullYear();
    if (isDmyOrder) {
      return dateFormat === "DMY_DOT" ? `${day}.${month}.${year}` : `${day}/${month}/${year}`;
    }
    return `${month}/${day}/${year}`;
  };

  const parseDateFromInput = valueDate => {
    if (mask.length === 4) {
      return valueDate;
    }

    const parts = String(valueDate || "").split(separator);
    if (parts.length !== 3) return null;

    const [p1, p2, p3] = parts;
    const month = isDmyOrder ? p2 : p1;
    const day = isDmyOrder ? p1 : p2;
    const year = String(p3 || "").trim();
    if (!/^\d{4}$/.test(year)) {
      return null;
    }
    const yearNum = Number(year);
    if (yearNum < 1000 || yearNum > 9999) {
      return null;
    }

    const monthNum = Number(month);
    const dayNum = Number(day);
    if (!Number.isFinite(monthNum) || !Number.isFinite(dayNum) || monthNum < 1 || monthNum > 12 || dayNum < 1 || dayNum > 31) {
      return null;
    }

    const date = new Date(yearNum, monthNum - 1, dayNum);
    if (Number.isNaN(date.getTime())) {
      return null;
    }
    if (date.getFullYear() !== yearNum || date.getMonth() !== monthNum - 1 || date.getDate() !== dayNum) {
      return null;
    }
    return date;
  };

  const toggleActive = () => {
    if (readOnly) {
      return;
    }

    let dateToSet = parseDateFromInput(inputValue);

    if (!dateToSet && value) {
      dateToSet = typeof value === "string" ? parseDateFromInput(value) : value;
    }

    setSelectedDate(dateToSet);
    setIsCalendarVisible(prev => !prev);
  };

  useEffect(() => {
    if (!inputValue && value) {
      setInputValue(formatDate(value));
    }

    if (inputValue && value && inputValue !== value) {
      setInputValue(formatDate(value));
    }
  }, [value, dateFormat]);

  const getMaxDateForTypedInput = () => {
    if (defaultMaxDate !== undefined) {
      return defaultMaxDate;
    }
    return null;
  };

  /** Builds MDY or DMY from digits only (auto-inserts separators while typing). */
  const formatDigitsAsMaskedDate = digitsRaw => {
    const digits = String(digitsRaw || "")
      .replace(/\D/g, "")
      .slice(0, 8);
    if (isDmyOrder) {
      const sep = separator;
      if (digits.length <= 2) return digits;
      if (digits.length <= 4) return `${digits.slice(0, 2)}${sep}${digits.slice(2)}`;
      return `${digits.slice(0, 2)}${sep}${digits.slice(2, 4)}${sep}${digits.slice(4)}`;
    }
    if (digits.length <= 2) return digits;
    if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
    return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
  };

  const handleInputChange = e => {
    if (readOnly) {
      return;
    }

    const raw = e.target.value;

    if (mask.length === 4) {
      const digitsOnly = raw.replace(/\D/g, "").slice(0, 4);
      setInputValue(digitsOnly);
      if (handleChangeInput) {
        handleChangeInput(digitsOnly);
      }
      return;
    }

    if (mask.length !== 10) {
      setInputValue(raw);
      if (handleChangeInput) {
        handleChangeInput(raw);
      }
      return;
    }

    const formatted = formatDigitsAsMaskedDate(raw);
    setInputValue(formatted);

    if (handleChangeInput) {
      if (formatted.length < 10) {
        handleChangeInput(formatted);
      } else {
        const inputDate = parseDateFromInput(formatted);
        const maxDate = getMaxDateForTypedInput();
        const dayOnly = d => (d ? new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime() : null);
        let withinMax = maxDate === null;
        if (!withinMax && inputDate && maxDate) {
          const inputDay = dayOnly(inputDate);
          const maxDay = dayOnly(maxDate);
          withinMax = inputDay !== null && maxDay !== null && inputDay <= maxDay;
        }

        if (!enforceMaxDateOnTypedComplete || withinMax || !inputDate) {
          handleChangeInput(formatted);
        }
      }
    }
  };

  const handleClickOutside = event => {
    if (calendarRef.current && !calendarRef.current.contains(event.target)) {
      setIsCalendarVisible(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const customNavigationLabel = ({ date, view }) => {
    const year = date.getFullYear();
    const currentYear = new Date().getFullYear();

    if (year === 2021 && view === "decade") {
      return `2021 – ${currentYear}`;
    }

    const previousDecadeStart = Math.floor((year - 1) / 10) * 10;
    return `${previousDecadeStart + 1} – ${previousDecadeStart + 10}`;
  };

  return (
    <div className={styles.parent}>
      <div className={styles.inputParent}>
        <Icon className={cn(styles.calendarIcon, readOnly && styles.calendarIconReadOnly)} onClick={toggleActive} aria-hidden />
        <input
          className={cn(styles.input, isInvalid ? styles.inputInvalid : "", inputClassName, readOnly && styles.inputReadOnly)}
          value={inputValue}
          onChange={handleInputChange}
          readOnly={readOnly}
          onBlur={() => {
            if (readOnly) {
              return;
            }
            const trimmed = String(inputValue || "").trim();
            if (!trimmed) {
              return;
            }
            const date = parseDateFromInput(inputValue);
            if (date) {
              const normalized = formatDate(date);
              setInputValue(normalized);
              setSelectedDate(date);
              onChange(normalized);
              if (handleChangeInput) {
                handleChangeInput(normalized);
              }
              scheduleSubmitIfPresent();
            }
          }}
          placeholder={placeholder}
          id={id}
          name={name}
          aria-label={ariaLabel}
          aria-labelledby={ariaLabelledBy}
          aria-invalid={isInvalid && errorId ? true : undefined}
          aria-describedby={isInvalid && errorId ? errorId : undefined}
        />
      </div>

      {!readOnly && isCalendarVisible && (
        <div className={cn(styles.calendar, openDirection === "up" ? styles.calendarUp : "")} ref={calendarRef}>
          {isYear ? (
            <Calendar
              defaultView="decade"
              maxDate={defaultMaxDate}
              onClickYear={handleDateChange}
              value={selectedDate ? format(new Date(selectedDate).toLocaleDateString("en-US"), "yyyy") : selectedDate}
              format="9999"
              minDetail="decade"
              maxDetail="decade"
              minDate={minDate}
              tileClassName={({ date }) => (isFutureDate(date) && isHideFeatureDate ? styles.hide : "")}
              navigationLabel={customNavigationLabel}
            />
          ) : (
            <Calendar maxDate={defaultMaxDate} minDate={minDate} onChange={handleDateChange} value={selectedDate} />
          )}
        </div>
      )}
      {isInvalid && (
        <div id={errorId} className={cn(styles.error, classNameError)}>
          {getSafeErrorText(error)}
        </div>
      )}
    </div>
  );
};

export default CalendarWithInputMask;
